﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿﻿import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Санитизация URL для защиты от XSS (javascript:, data:, vbscript: и т.д.).
 * @param {string} url
 * @returns {string|null} — безопасный URL или null, если URL опасен
 */
function sanitizeUrl(url) {
    if (!url) return null;
    const trimmed = url.trim();

    // Внутренние ссылки (начинаются с /) — безопасны, но не /evil.com
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
        return trimmed;
    }

    // Разрешённые протоколы
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'ftp:'];
    try {
        const parsed = new URL(trimmed);
        if (allowedProtocols.includes(parsed.protocol)) {
            return trimmed;
        }
    } catch {
        // Невалидный URL — не вставляем в href
        return null;
    }

    return null;
}

/**
 * Парсит текст с markdown-lite разметкой и возвращает массив React-элементов.
 * Поддерживает:
 *  - `# Heading` ... `###### Heading` → <h1>...<h6>
 *  - `---` → <hr> (горизонтальный разделитель, на отдельной строке)
 *  - `[text](url)` → <a> (внешние) или <Link> (внутренние, начинаются с /)
 *  - `*bold*` → <strong>
 *  - `_italic_` → <em>
 *  - `~~strike~~` → <s>
 *  - `` `code` `` → <code>
 *  - `[quote]...[/quote]` → <blockquote>
 *
 * Порядок обработки: header → hr → quote → code → links → bold → italic → strike → mentions.
 * Вложенные конструкции не поддерживаются.
 *
 * @param {string} text — исходный текст с разметкой
 * @returns {Array<React.ReactNode>} — массив React-нод для рендера
 */
export function renderMarkdownLite(text) {
    if (!text) return [text];

    // 0. Извлекаем заголовки # ... ###### (самые приоритетные, блочные)
    const headerRegex = /^(#{1,6})\s+(.+)$/gm;
    const headerParts = [];
    let hLastIndex = 0;
    let hMatch;

    while ((hMatch = headerRegex.exec(text)) !== null) {
        if (hMatch.index > hLastIndex) {
            headerParts.push({ type: 'text', value: text.slice(hLastIndex, hMatch.index) });
        }
        headerParts.push({ type: 'header', level: hMatch[1].length, value: hMatch[2] });
        hLastIndex = hMatch.index + hMatch[0].length;
    }
    if (hLastIndex < text.length) {
        headerParts.push({ type: 'text', value: text.slice(hLastIndex) });
    }
    if (headerParts.length === 0) {
        headerParts.push({ type: 'text', value: text });
    }

    // 0b. Извлекаем горизонтальные линии --- (блочные)
    const hrParts = [];
    const hrRegex = /^-{3,}$/gm;

    headerParts.forEach(hPart => {
        if (hPart.type === 'header') {
            hrParts.push(hPart);
            return;
        }
        const textValue = hPart.value;
        hrRegex.lastIndex = 0;
        let hrLastIndex = 0;
        let hrMatch;

        while ((hrMatch = hrRegex.exec(textValue)) !== null) {
            if (hrMatch.index > hrLastIndex) {
                hrParts.push({ type: 'text', value: textValue.slice(hrLastIndex, hrMatch.index) });
            }
            hrParts.push({ type: 'hr' });
            hrLastIndex = hrMatch.index + hrMatch[0].length;
        }
        if (hrLastIndex < textValue.length) {
            hrParts.push({ type: 'text', value: textValue.slice(hrLastIndex) });
        }
        // Если ничего не было добавлено — сохраняем оригинальный фрагмент
        if (hrParts.length === 0) {
            hrParts.push(hPart);
        }
    });

    if (hrParts.length === 0) {
        hrParts.push({ type: 'text', value: text });
    }

    // 1. Извлекаем [quote]...[/quote] (в текстовых фрагментах)
    const quoteRegex = /\[quote\]([\s\S]*?)\[\/quote\]/gi;
    const quoteParts = [];

    hrParts.forEach(hPart => {
        if (hPart.type !== 'text') {
            quoteParts.push(hPart);
            return;
        }
        const textValue = hPart.value;
        quoteRegex.lastIndex = 0;
        let qLastIndex = 0;
        let qMatch;

        while ((qMatch = quoteRegex.exec(textValue)) !== null) {
            if (qMatch.index > qLastIndex) {
                quoteParts.push({ type: 'text', value: textValue.slice(qLastIndex, qMatch.index) });
            }
            quoteParts.push({ type: 'quote', value: qMatch[1].trim() });
            qLastIndex = qMatch.index + qMatch[0].length;
        }
        if (qLastIndex < textValue.length) {
            quoteParts.push({ type: 'text', value: textValue.slice(qLastIndex) });
        }
    });

    if (quoteParts.length === 0) {
        quoteParts.push({ type: 'text', value: text });
    }

    // 2. Извлекаем все `code` (самые приоритетные внутри текстовых фрагментов)
    const codeRegex = /(`+)((?:[^`]|(?!\1)[\s\S])*?)\1/g;
    const codeParts = [];
    let lastIndex = 0;
    let match;

    quoteParts.forEach(qPart => {
        if (qPart.type !== 'text') {
            codeParts.push(qPart);
            return;
        }
        const textValue = qPart.value;
        codeRegex.lastIndex = 0;
        lastIndex = 0;
        while ((match = codeRegex.exec(textValue)) !== null) {
            if (match.index > lastIndex) {
                codeParts.push({ type: 'text', value: textValue.slice(lastIndex, match.index) });
            }
            codeParts.push({ type: 'code', value: match[2] });
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < textValue.length) {
            codeParts.push({ type: 'text', value: textValue.slice(lastIndex) });
        }
    });

    // 3. В текстовых фрагментах парсим [text](url)
    const linkParts = codeParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        // [text](url) — text не содержит ], url не содержит )
        const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
        let idx = 0;
        let m;
        while ((m = linkRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'link', value: m[1], url: m[2] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 4. В текстовых фрагментах парсим *bold*
    const boldParts = linkParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        const boldRegex = /\*([^*]+)\*/g;
        let idx = 0;
        let m;
        while ((m = boldRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'bold', value: m[1] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 5. В текстовых фрагментах парсим _italic_
    const italicParts = boldParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        const italicRegex = /_([^_]+)_/g;
        let idx = 0;
        let m;
        while ((m = italicRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'italic', value: m[1] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 6. В текстовых фрагментах парсим ~~strikethrough~~
    const strikeParts = italicParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        const strikeRegex = /~~([^~]+)~~/g;
        let idx = 0;
        let m;
        while ((m = strikeRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'strike', value: m[1] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 7. В текстовых фрагментах парсим @mentions
    const mentionParts = strikeParts.flatMap(part => {
        if (part.type !== 'text') return [part];
        const result = [];
        // @Name — буквы, цифры, пробелы, дефис, подчёркивание, точка (до 40 символов)
        const mentionRegex = /@([\p{L}\p{N}][\p{L}\p{N}\s._-]{0,39})/gu;
        let idx = 0;
        let m;
        while ((m = mentionRegex.exec(part.value)) !== null) {
            if (m.index > idx) {
                result.push({ type: 'text', value: part.value.slice(idx, m.index) });
            }
            result.push({ type: 'mention', value: m[0] });
            idx = m.index + m[0].length;
        }
        if (idx < part.value.length) {
            result.push({ type: 'text', value: part.value.slice(idx) });
        }
        if (result.length === 0) result.push(part);
        return result;
    });

    // 8. Превращаем токены в React-элементы
    return mentionParts.map((part, i) => {
        switch (part.type) {
            case 'header': {
                const Tag = `h${part.level}`;
                const headerStyles = {
                    1: { fontSize: '1.8rem', fontWeight: 700, color: '#e9ecef', margin: '0.5em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal', borderBottom: '2px solid #4dabf7', paddingBottom: '0.3em' },
                    2: { fontSize: '1.5rem', fontWeight: 700, color: '#e9ecef', margin: '0.8em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal', borderBottom: '1px solid #3d4552', paddingBottom: '0.25em' },
                    3: { fontSize: '1.3rem', fontWeight: 700, color: '#e9ecef', margin: '0.8em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal' },
                    4: { fontSize: '1.1rem', fontWeight: 700, color: '#e9ecef', margin: '0.6em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal' },
                    5: { fontSize: '1rem', fontWeight: 700, color: '#e9ecef', margin: '0.6em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal' },
                    6: { fontSize: '0.9rem', fontWeight: 700, color: '#adb5bd', margin: '0.6em 0 0.4em', lineHeight: 1.3, whiteSpace: 'normal' },
                };
                return React.createElement(Tag, {
                    key: i,
                    className: `md-header md-header-h${part.level}`,
                    style: headerStyles[part.level]
                }, renderMarkdownLite(part.value));
            }
            case 'hr':
                return React.createElement('hr', { key: i });
            case 'quote':
                return React.createElement('blockquote', {
                    key: i,
                    className: 'md-quote'
                }, renderMarkdownLite(part.value));
            case 'bold':
                return React.createElement('strong', { key: i }, part.value);
            case 'italic':
                return React.createElement('em', { key: i }, part.value);
            case 'strike':
                return React.createElement('s', { key: i }, part.value);
            case 'code':
                return React.createElement('code', { key: i }, part.value);
            case 'link': {
                const safeUrl = sanitizeUrl(part.url);
                if (!safeUrl) {
                    // Опасный или невалидный URL — показываем как текст
                    return part.value;
                }
                const isInternal = safeUrl.startsWith('/') && !safeUrl.startsWith('//');
                if (isInternal) {
                    return React.createElement(Link, { key: i, to: safeUrl }, part.value);
                }
                return React.createElement('a', {
                    key: i,
                    href: safeUrl,
                    target: '_blank',
                    rel: 'noopener noreferrer'
                }, part.value);
            }
            case 'mention':
                return React.createElement('span', { key: i, className: 'chat-message__mention' }, part.value);
            case 'text':
            default:
                return part.value;
        }
    });
}
